import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { ParentContext } from '../notes-list/notes.model';
import {
  ComponentChanges,
  FormModelControl,
  SelectOption,
  SnackbarService,
} from '@bcl/design-system';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  Action,
  ActionsList,
  ActionsListFilter,
  DateRange,
} from 'src/app/pages/action/action.model';
import {
  BehaviorSubject,
  combineLatest,
  ReplaySubject,
  takeUntil,
  Subject,
} from 'rxjs';
import {
  RequiredClaimsService,
  CurrentUserService,
  IUser,
} from '@ews/users-management';
import { MatDialog } from '@angular/material/dialog';
import { Claims } from 'src/app/authorization';
import { ActionsListPageChanged, ActionsPermissions } from './actions.model';
import { ActionApiService } from 'src/app/pages/action/action.service';
import { Router } from '@angular/router';
import { ActionDialogComponent } from './action-dialog/action-dialog.component';
import { AccordionService } from 'src/app/services/accordion.service';
import { OnDestroy } from '@angular/core';

@Component({
  selector: 'app-actions-list',
  templateUrl: './actions-list.component.html',
  styleUrls: ['./actions-list.component.scss'],
})
export class ActionsListComponent implements OnChanges, OnInit, OnDestroy {
  @Input()
  public titleLabel?: string;
  @Input()
  public actionsList: ActionsList = { actions: [], totalEntries: 0 };
  @Input()
  public canAdd = false;
  @Input()
  public canEdit = false;
  @Input()
  public canDelete = false;
  @Input()
  public pageSize = 5;
  @Input()
  public parentContext!: ParentContext;
  @Input()
  public customerId?: number;
  @Input()
  public locationId?: number;
  @Input()
  public areaId?: number;
  @Input()
  public dateFilterOptions: SelectOption[] = [];
  @Input()
  public statusFilterOptions: SelectOption[] = [];
  @Input()
  public showItemList = true;
  @Input()
  public backUrl = '';
  @Input()
  public visit?: any; // Add visit input
  @Input()
  public isEditingDisabled = false;

  @Output()
  public loadMore = new EventEmitter<ActionsListPageChanged>();
  @Output()
  public toggleVisibility = new EventEmitter<void>();

  public readonly actions$ = new BehaviorSubject<Action[]>([]);

  public permissions$ = new ReplaySubject<ActionsPermissions>(1);

  public readonly filterForm: FormGroup<FormModelControl<ActionsListFilter>>;

  public user: IUser | null = null;

  private currentPage = 1;
  private lastPage = 0;
  private destroyed$ = new Subject<void>();

  private defaultDateFilter: DateRange | null = '4';

  constructor(
    public dialog: MatDialog,
    requiredClaimsService: RequiredClaimsService,
    private actionsService: ActionApiService,
    fb: FormBuilder,
    public snackbarService: SnackbarService,
    private router: Router,
    private accordionService: AccordionService,
    private currentUserService: CurrentUserService
  ) {
    this.filterForm = fb.group({
      date: new FormControl<DateRange | null>(this.defaultDateFilter),
      status: new FormControl<string | null>('0'),
    });

    this.filterForm.valueChanges.subscribe((x) => {
      this.currentPage = 1;
      this.loadMore.next({
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        filter: { ...x },
      });
    });
    combineLatest([
      requiredClaimsService.hasAccess(Claims.CreateAction),
      requiredClaimsService.hasAccess(Claims.UpdateAction),
      requiredClaimsService.hasAccess(Claims.DeleteAction),
    ]).subscribe(([canAdd, canEdit, canDelete]) => {
      this.canAdd = canAdd;
      this.canEdit = canEdit;
      this.canDelete = canDelete;
    });
  }

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }
  public get hasNextPage(): boolean {
    return this.currentPage < this.lastPage;
  }
  public ngOnInit(): void {
    this.loadMore.emit({
      pageNumber: 1,
      pageSize: this.pageSize,
      filter: {
        date: '4',
        status: '0',
      },
    });

    // Subscribe to current user service
    this.currentUserService.user
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user = user;
      });

    // Subscribe to accordion service to manage showItemList state
    this.accordionService
      .getCurrentOpenComponent()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((openComponentId) => {
        this.showItemList = openComponentId === 'actions';
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
  public ngOnChanges(changes: ComponentChanges<ActionsListComponent>): void {
    const actionsList = changes.actionsList?.currentValue;
    if (actionsList) {
      this.actions$.next([
        ...(this.currentPage > 1 ? this.actions$.value : []),
        ...actionsList.actions,
      ]);
      this.lastPage = Math.ceil(actionsList.totalEntries / this.pageSize);
    }
  }
  showItems() {
    // Use accordion service to manage state
    if (this.showItemList) {
      this.accordionService.closeComponent('actions');
    } else {
      this.accordionService.openComponent('actions');
    }
  }

  public loadMoreActions(): void {
    this.currentPage++;
    this.loadMore.next({
      pageNumber: this.currentPage,
      pageSize: this.pageSize,
      filter: { ...this.filterForm.value },
    });
  }

  public editAction(action?: Action): void {
    this.router.navigate(['action'], {
      state: {
        backUrl: this.backUrl,
        parentObject: this.parentContext.parentObject,
        parentId: this.parentContext.parentId,
        customerId: this.customerId || action?.customerId,
        locationId: this.locationId || action?.locationId,
        areaId: this.areaId || action?.areaId,
        actionId: action?.id ?? 0,
      },
    });
  }

  public approveByEWS(action: Action): void {
    const updateData = {
      id: action.id,
      ['number']: action.number || '',
      actionTypeID: action.actionTypeId?.toString(),
      statusID: action.statusId?.toString(),
      responsibilityID: action.responsibilityId?.toString(),
      deadline: action.deadline?.toString(),
      deadlineTime: action.deadlineTime,
      duration: action.duration,
      title: action.title,
      description: action.description,
      assigneeID: action.assigneeId,
      customerID: action.customerId,
      locationID: action.locationId,
      areaID: action.areaId,
      contactId: action.contactId,
      initialApprovalBy: undefined,
      initialApprovalDate: undefined,
      finalApprovalBy: this.user?.id,
      finalApprovalDate: new Date().toISOString().slice(0, 16),
      reason: action.reason,
      priorityId: action.priorityId,
    };
    this.actionsService.updateAction(updateData).subscribe(() => {
      this.snackbarService.open(
        'green',
        'ACTIONS_LIST.MESSAGES.ACTION_APPROVED_SUCCESSFULLY'
      );
      // Reset to first page and reload the action list after successful approval
      this.currentPage = 1;
      this.loadMore.emit({
        pageNumber: 1,
        pageSize: this.pageSize,
        filter: { ...this.filterForm.value },
      });
    });
  }

  public showActionDialog(type: string, action: Action | null): void {
    const dialogRef = this.dialog.open(ActionDialogComponent, {
      disableClose: true,
      data: { action, actionType: type },
    });

    dialogRef
      .afterClosed()
      .subscribe(
        (
          result:
            | { actionType: 'ADD' | 'UPDATE' | 'DELETE'; action: Action }
            | false
        ) => {
          if (result) {
            switch (result.actionType) {
              case 'DELETE':
                this.actionsService.deleteAction(action.id).subscribe(() => {
                  this.loadMore.emit({
                    pageNumber: this.currentPage,
                    pageSize: this.pageSize,
                    filter: { ...this.filterForm.value },
                  });
                  this.snackbarService.open(
                    'green',
                    'ACTIONS.LIST.MESSAGES.ACTION_DELETED_SUCCESSFULLY'
                  );
                });
                break;
            }
          }
        }
      );
  }

  public resetFiltersAndRefresh(): void {
    this.currentPage = 1;
    this.filterForm.patchValue({
      date: '4',
      status: '0',
    });
  }
}
